<script setup lang="js">
  import { computed, defineExpose, defineProps, onMounted, ref } from 'vue';
  import html2pdf from 'html2pdf.js';
  import { getSession } from '@/indexedDB';

  const props = defineProps({
    info: Object,
    shipping: Object,
    fee: Object,
  });

  const pdfRef = ref(null);
  const additionalFields = {
    georgy: [],
    poland: [
      {
        id: 'expeditionCost',
        model: 'expeditionCost',
        title: 'Transport to Poland ($)',
      },
      {
        id: 'customsCost',
        model: 'customsCost',
        title: 'Customs fees ($)',
      },
      {
        id: 'rotterdamComplex',
        model: 'certification',
        title: 'Rotterdam Complex ($)',
      },
      {
        id: 'serviceCost',
        model: 'serviceCost',
        title: 'Service fee ($)',
      },
    ],
    ukraine: [
      {
        id: 'year',
        model: 'expeditionCost',
        title: 'Shipping fees ($)',
      },
      {
        id: 'bid',
        model: 'parkingCost',
        title: 'Parking cost ($)',
      },
      {
        id: 'auctionFee',
        model: 'towingCost',
        title: 'Towing service ($)',
      },
      {
        id: 'customsCost',
        model: 'customsCost',
        title: 'Customs fees ($)',
      },
      {
        id: 'certification',
        model: 'certification',
        title: 'Certification cost ($)',
      },
      {
        id: 'pensionCost',
        model: 'pensionCost',
        title: 'Pension fee ($)',
      },
      {
        id: 'serviceCost',
        model: 'serviceCost',
        title: 'Service fee ($)',
      },
    ],
  };
  const waitForImage = (el) => {
    return new Promise((resolve) => {
      const imgs = el.getElementsByTagName('img');
      let loaded = 0;

      if (imgs.length === 0) return resolve();

      for (let i = 0; i < imgs.length; i++) {
        if (imgs[i].complete) {
          loaded++;
          if (loaded === imgs.length) resolve();
        } else {
          imgs[i].addEventListener('load', () => {
            loaded++;
            if (loaded === imgs.length) resolve();
          });
          imgs[i].addEventListener('error', () => {
            loaded++;
            if (loaded === imgs.length) resolve();
          });
        }
      }
    });
  };

  const generatePDF = async () => {
    if (!pdfRef.value) return;

    await waitForImage(pdfRef.value);

    html2pdf()
      .set({
        margin: 0.5,
        filename: `calc-${Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      })
      .from(pdfRef.value)
      .save();
  };

  defineExpose({ generatePDF });
  const sessionAdditional = ref({});
  onMounted(async () => {
    sessionAdditional.value = await getSession('additionalFields');
  });

  const total = computed(() => {
    return (
      (+props.info.priceNow || 0) +
      (props.shipping.shippingByPhoto && props.shipping.shippingByPhoto[0]
        ? +props.shipping.costShippingInsurance
        : 0) +
      (+props.shipping.cost || 0) +
      (+props.info.auctionFee || 0) +
      (+props.shipping.bodyPrice || 0) +
      (+props.shipping.costShippingInsurance || 0) +
      (+sessionAdditional.value.customsCost || 0) +
      (+sessionAdditional.value.pensionCost || 0) +
      (+sessionAdditional.value.certification || 0) +
      (+sessionAdditional.value.expeditionCost || 0) +
      (+sessionAdditional.value.parkingCost || 0) +
      (+sessionAdditional.value.serviceCost || 0) +
      (+sessionAdditional.value.towingCost || 0)
    ).toFixed(2);
  });
</script>

<template>
  <div
    ref="pdfRef"
    class="bg-white p-6 rounded shadow max-w-[800px] max-h-[85vh] overflow-auto text-[14px] leading-relaxed font-sans"
  >
    <div class="flex items-start gap-4 mb-4">
      <img :src="info.img" alt="car image" class="w-40 rounded shadow border" />
      <div>
        <h2 class="text-xl font-bold mb-2">{{ info.name }}</h2>
        <p><strong>Year:</strong> {{ info.yearBorn }}</p>
        <p><strong>Engine:</strong> {{ info.eng }} ({{ info.typeEng }})</p>
        <p><strong>Body Type:</strong> {{ shipping.body }}</p>
        <p><strong>VIN:</strong> {{ info.vim }}</p>
      </div>
    </div>
    <div class="mb-4">
      <h3 class="font-semibold text-lg">📦 Car Location:</h3>
      <p><strong>Auction:</strong> {{ info.full_cc }}</p>
      <p>
        <strong>City:</strong> {{ info.city }}, {{ info.state }} ({{
          info.country
        }})
      </p>
    </div>

    <div class="mb-4">
      <h3 class="font-semibold text-lg">💰 Payments and Shipping:</h3>
      <p><strong>Final Bid:</strong> ${{ info.priceNow }}</p>
      <p><strong>Auction Fee:</strong> ${{ info.auctionFee }}</p>
      <p>
        <strong>Shipping Insurance:</strong> ${{
          shipping.costShippingInsurance
        }}
      </p>
      <p>
        <strong
          >Port{{
            shipping.port !== 'Ask company' ? `(${shipping.port})` : ''
          }}:</strong
        >
        {{ shipping.cost ? `$${shipping.cost}` : 'need confirm' }}
      </p>
      <div>
        <p v-for="field in additionalFields[info?.countryCalc]" :key="field.id">
          <strong>{{ field.title }}:</strong>
          {{ sessionAdditional[field.model] }}
        </p>
      </div>
    </div>
    <div><b> Total</b> : ${{ total }}</div>
    <div class="text-right text-gray-500 text-sm mt-4">
      <p>Calculation Date: {{ new Date().toLocaleDateString() }}</p>
    </div>
  </div>
</template>
